import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Search } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import {
  listNodes,
  bulkUpdate,
  decommissionNode,
  type NodeStatus,
  type Platform
} from '@/api/fleet';
import { PLATFORM_META, STATUS_BADGE, formatHashrate, timeAgo } from '@/lib/format';
import { RegisterNodesModal } from './RegisterNodesModal';
import { NodeDetailModal } from './NodeDetailModal';

const PLATFORM_OPTS: Array<Platform | ''> = ['', 'linux', 'windows', 'mac'];
const STATUS_OPTS: Array<NodeStatus | ''> = ['', 'active', 'paused', 'offline'];

export function NodesScreen() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<NodeStatus | ''>('');
  const [platformFilter, setPlatformFilter] = useState<Platform | ''>('');
  const [search, setSearch] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [bulkSelection, setBulkSelection] = useState<string[]>([]);

  const limit = 25;
  const { data, isLoading } = useQuery({
    queryKey: ['fleet-nodes', { page, statusFilter, platformFilter, search }],
    queryFn: () =>
      listNodes({
        page,
        limit,
        status: statusFilter || undefined,
        platform: platformFilter || undefined,
        search: search || undefined
      })
  });

  const nodes = data?.docs ?? [];
  const totalPages = data?.totalPages ?? 1;

  const bulkMut = useMutation({
    mutationFn: (status: NodeStatus) =>
      bulkUpdate({ filter: { nodeKeys: bulkSelection }, update: { status } }),
    onSuccess: (_, status) => {
      toast.success(`Bulk ${status}d ${bulkSelection.length} node(s)`);
      setBulkSelection([]);
      qc.invalidateQueries({ queryKey: ['fleet-nodes'] });
      qc.invalidateQueries({ queryKey: ['fleet-stats'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Bulk update failed')
  });

  const decommissionMut = useMutation({
    mutationFn: (nodeKey: string) => decommissionNode(nodeKey),
    onSuccess: () => {
      toast.success('Node decommissioned');
      setSelectedKey(null);
      qc.invalidateQueries({ queryKey: ['fleet-nodes'] });
      qc.invalidateQueries({ queryKey: ['fleet-stats'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Decommission failed')
  });

  const allSelected =
    nodes.length > 0 && bulkSelection.length === nodes.length;
  const toggleAll = () => {
    if (allSelected) setBulkSelection([]);
    else setBulkSelection(nodes.map(n => n.nodeKey));
  };

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="bottom-right" richColors />

      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-nurev-text">Nodes</h1>
          <p className="mt-1 text-sm text-nurev-muted">
            {data?.totalDocs ?? '—'} total · {bulkSelection.length} selected
          </p>
        </div>
        <button onClick={() => setShowRegister(true)} className="btn-primary">
          <Plus className="h-4 w-4" /> Register nodes
        </button>
      </header>

      {/* Filter bar */}
      <div className="card flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-nurev-muted" />
          <input
            type="text"
            placeholder="Search by label, key, or hostname…"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="input pl-10"
          />
        </div>
        <div className="flex gap-1.5">
          {STATUS_OPTS.map(s => (
            <button
              key={s || 'all'}
              onClick={() => {
                setStatusFilter(s);
                setPage(1);
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium ${
                statusFilter === s
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-white/[0.04] text-nurev-muted hover:text-nurev-text'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5">
          {PLATFORM_OPTS.map(p => (
            <button
              key={p || 'allp'}
              onClick={() => {
                setPlatformFilter(p);
                setPage(1);
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium ${
                platformFilter === p
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-white/[0.04] text-nurev-muted hover:text-nurev-text'
              }`}
            >
              {p ? PLATFORM_META[p].emoji : '🌐'}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk action bar */}
      {bulkSelection.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
          <span className="text-sm text-emerald-300">
            {bulkSelection.length} selected
          </span>
          <button
            onClick={() => bulkMut.mutate('active')}
            disabled={bulkMut.isPending}
            className="btn-secondary text-xs"
          >
            Resume
          </button>
          <button
            onClick={() => bulkMut.mutate('paused')}
            disabled={bulkMut.isPending}
            className="btn-secondary text-xs"
          >
            Pause
          </button>
          <button
            onClick={() => setBulkSelection([])}
            className="ml-auto text-xs text-nurev-muted hover:text-nurev-text"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-3 py-12 text-nurev-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading nodes…
          </div>
        ) : nodes.length === 0 ? (
          <div className="py-12 text-center text-sm text-nurev-muted">
            No nodes match these filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border bg-white/[0.02] text-left text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-3 py-3">Label / Key</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Platform</th>
                <th className="px-3 py-3">Group</th>
                <th className="px-3 py-3 text-right">Speed</th>
                <th className="px-3 py-3 text-right">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map(n => (
                <tr
                  key={n.nodeKey}
                  onClick={() => setSelectedKey(n.nodeKey)}
                  className="cursor-pointer border-b border-nurev-border/60 hover:bg-white/[0.02]"
                >
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={bulkSelection.includes(n.nodeKey)}
                      onChange={() =>
                        setBulkSelection(prev =>
                          prev.includes(n.nodeKey)
                            ? prev.filter(k => k !== n.nodeKey)
                            : [...prev, n.nodeKey]
                        )
                      }
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-nurev-text">
                      {n.label || n.hostname || n.nodeKey.slice(0, 12)}
                    </div>
                    <div className="font-mono text-[10px] text-nurev-muted">
                      {n.nodeKey.slice(0, 24)}…
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
                        STATUS_BADGE[n.status] || ''
                      }`}
                    >
                      {n.status}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {PLATFORM_META[n.platform]?.emoji}{' '}
                    <span className="text-nurev-text-soft">
                      {PLATFORM_META[n.platform]?.label || n.platform}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-nurev-text-soft">{n.group}</td>
                  <td className="px-3 py-3 text-right font-mono text-emerald-300">
                    {formatHashrate(n.stats.hashrate)}
                  </td>
                  <td className="px-3 py-3 text-right text-nurev-muted">
                    {timeAgo(n.lastHeartbeatAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-nurev-border px-4 py-3 text-xs text-nurev-muted">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-ghost text-xs disabled:opacity-40"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-ghost text-xs disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showRegister && (
        <RegisterNodesModal
          onClose={() => setShowRegister(false)}
          onSuccess={() => {
            setShowRegister(false);
            qc.invalidateQueries({ queryKey: ['fleet-nodes'] });
            qc.invalidateQueries({ queryKey: ['fleet-stats'] });
          }}
        />
      )}
      {selectedKey && (
        <NodeDetailModal
          nodeKey={selectedKey}
          onClose={() => setSelectedKey(null)}
          onDecommission={() => decommissionMut.mutate(selectedKey)}
        />
      )}
    </div>
  );
}
